import { watch, onUnmounted } from 'vue'
import { useBulkDownloadTrackerStore } from '@/stores/bulk-download-tracker.store'
import { useUserStore } from '@/features/user/stores/user.store'
import { BulkDownloadDetailService } from '@/features/purchasehistory/services/bulk-download-detail.service'
import {
  isDeliveredStatus,
  isTerminalStatus,
} from '@/features/purchasehistory/utils/bulk-download-status'

// This is a background balance check, not the progress bar — a relaxed cadence is plenty and
// keeps it from hammering the status endpoint across the whole session. The detail page keeps
// its own faster 2s poll for live progress.
const CREDIT_SYNC_INTERVAL_MS = 10000

/**
 * App-shell background poll that keeps the header credit balance in sync with bulk downloads
 * finishing anywhere in the session. Mounted ONCE (DefaultLayout).
 *
 * A bulk download spends credits only when the server-side job completes — after the export
 * request has already returned — so the balance the export showed is pre-spend. This watches the
 * ids registered in `useBulkDownloadTrackerStore` and, the moment one reaches a delivered status,
 * re-fetches the user's credits so the top-bar numbers update without any page reload. Jobs that
 * reach any terminal status (including failure) are dropped from the watch list either way.
 */
export function useBulkDownloadCreditSync() {
  const tracker = useBulkDownloadTrackerStore()
  const userStore = useUserStore()
  const service = new BulkDownloadDetailService()

  let timer: ReturnType<typeof setTimeout> | null = null
  let polling = false
  let unmounted = false

  function stop(): void {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  function scheduleNext(): void {
    stop()
    if (unmounted || tracker.pendingIds.length === 0) return
    timer = setTimeout(() => void pollOnce(), CREDIT_SYNC_INTERVAL_MS)
  }

  async function pollOnce(): Promise<void> {
    // Guard against overlapping runs if a slow batch of requests outlasts the interval.
    if (polling || unmounted) return
    polling = true

    // Snapshot so ids added mid-poll are picked up on the next tick, not mutated underfoot.
    const ids = [...tracker.pendingIds]
    let anyDelivered = false

    await Promise.all(
      ids.map(async (id) => {
        try {
          const response = await service.refreshBulkDownloadPercentage(id)
          const status = response?.information?.Status
          // A login redirect returns HTML with no information — leave the id tracked and retry.
          if (response?.information == null) return

          if (isDeliveredStatus(status)) {
            anyDelivered = true
            tracker.untrack(id)
          } else if (isTerminalStatus(status)) {
            // Terminal but not delivered (hard failure) — nothing was charged, so just stop
            // watching it.
            tracker.untrack(id)
          }
        } catch {
          // Transient failure (network blip, throttled background tab) — keep the id tracked
          // and try again on the next tick.
        }
      }),
    )

    polling = false
    if (unmounted) return

    // At least one job delivered → its credits were just consumed server-side, so pull the new
    // balance once for the whole batch.
    if (anyDelivered) {
      await userStore.fetchCredits()
    }

    scheduleNext()
  }

  // Start the loop whenever the first id appears; the loop self-stops once the list empties.
  watch(
    () => tracker.pendingIds.length,
    (count, previous) => {
      if (count > 0 && (previous === 0 || previous === undefined) && !timer) {
        scheduleNext()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    unmounted = true
    stop()
  })
}
